import { defineStore } from 'pinia'

import { applyBranding, applyThemeMode, type BrandingTokens } from '@/utils/applyBranding'
import { fetchUserStudios } from '@/services/studiosService'
import {
  getBranding,
  updateBranding,
  uploadBrandingLogo,
  type BrandingData,
} from '@/services/brandingService'

interface BrandingState {
  studios: { id: number; name: string }[]
  activeStudioId: string
  currentBranding: BrandingData | null
  originalBranding: BrandingData | null
  logoUrl: string | null
  loading: boolean
}

const DEFAULT_BRANDING: BrandingData = {
  primary_color: '#1f3a5f',
  secondary_color: '#4b7abf',
  background_color: '#f6f7fb',
  surface_color: '#ffffff',
  text_color: '#1d1f2a',
  muted_text_color: '#6b7280',
  border_color: '#e5e7eb',
  radius: 14,
  mode: 'light',
  logo_url: null,
}

const getActiveStudioId = () => localStorage.getItem('active_studio_id') ?? '1'

const brandingToTokens = (branding: BrandingData): BrandingTokens => ({
  bg: branding.background_color,
  surface: branding.surface_color,
  text: branding.text_color,
  muted: branding.muted_text_color,
  border: branding.border_color,
  primary: branding.primary_color,
  primary2: branding.secondary_color,
  radius: `${branding.radius}px`,
})

export const useBrandingStore = defineStore('branding', {
  state: (): BrandingState => ({
    studios: [],
    activeStudioId: getActiveStudioId(),
    currentBranding: null,
    originalBranding: null,
    logoUrl: null,
    loading: false,
  }),
  actions: {
    setActiveStudio(studioId: string) {
      this.activeStudioId = studioId
      localStorage.setItem('active_studio_id', studioId)
    },
    applyPreview(branding: BrandingData) {
      this.logoUrl = branding.logo_url ?? null
      applyBranding(brandingToTokens(branding))
      applyThemeMode(branding.mode)
    },
    loadFromCache(studioId: string) {
      const cacheKey = `branding_cache_${studioId}`
      const cached = localStorage.getItem(cacheKey)
      if (!cached) return false
      try {
        const parsed = JSON.parse(cached) as BrandingData
        if (parsed) {
          this.currentBranding = parsed
          this.applyPreview(parsed)
          return true
        }
      } catch {
        return false
      }
      return false
    },
    async loadStudios() {
      const studios = await fetchUserStudios()
      const sanitized = studios.filter(
        (studio) => studio && studio.id !== undefined && studio.id !== null,
      )
      this.studios = sanitized
      if (!sanitized.length) return
      const stored = getActiveStudioId()
      const exists = sanitized.some((studio) => studio.id.toString() === stored)
      const firstStudio = sanitized[0]
      if (!firstStudio) return
      const nextId = exists ? stored : firstStudio.id.toString()
      this.setActiveStudio(nextId)
    },
    async loadBranding(studioId?: string) {
      const id = studioId ?? this.activeStudioId ?? getActiveStudioId()
      this.setActiveStudio(id)
      const hadCache = this.loadFromCache(id)
      this.loading = true
      try {
        const response = await getBranding(id)
        const branding = response.data?.branding ?? this.currentBranding
        if (branding) {
          this.currentBranding = { ...DEFAULT_BRANDING, ...branding }
          this.originalBranding = { ...DEFAULT_BRANDING, ...branding }
          this.applyPreview(this.currentBranding)
          localStorage.setItem(`branding_cache_${id}`, JSON.stringify(this.currentBranding))
          return
        }
        if (!hadCache) {
          this.currentBranding = { ...DEFAULT_BRANDING }
          this.originalBranding = { ...DEFAULT_BRANDING }
          this.applyPreview(this.currentBranding)
        }
      } catch {
        if (!hadCache) {
          this.currentBranding = { ...DEFAULT_BRANDING }
          this.originalBranding = { ...DEFAULT_BRANDING }
          this.applyPreview(this.currentBranding)
        }
      } finally {
        this.loading = false
      }
    },
    restore() {
      if (!this.originalBranding) return
      this.currentBranding = { ...this.originalBranding }
      this.applyPreview(this.currentBranding)
    },
    async saveBranding() {
      if (!this.currentBranding) return null
      const response = await updateBranding(this.activeStudioId, this.currentBranding)
      const updated = response.data?.branding ?? this.currentBranding
      this.currentBranding = { ...DEFAULT_BRANDING, ...updated }
      this.originalBranding = { ...DEFAULT_BRANDING, ...updated }
      this.applyPreview(this.currentBranding)
      localStorage.setItem(`branding_cache_${this.activeStudioId}`, JSON.stringify(this.currentBranding))
      return this.currentBranding
    },
    async uploadLogo(file: File) {
      const response = await uploadBrandingLogo(this.activeStudioId, file)
      const branding = response.data?.branding
      if (branding) {
        this.currentBranding = { ...DEFAULT_BRANDING, ...branding }
      } else if (response.data?.logo_url && this.currentBranding) {
        this.currentBranding = { ...this.currentBranding, logo_url: response.data.logo_url }
      }
      if (this.currentBranding) {
        this.applyPreview(this.currentBranding)
        localStorage.setItem(
          `branding_cache_${this.activeStudioId}`,
          JSON.stringify(this.currentBranding),
        )
      }
      return this.currentBranding
    },
  },
})
